import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { 
  Loader2, Swords, Clock, CheckCircle, XCircle, ChevronRight, Check, X 
} from 'lucide-react'
import type { AdminMatchSummary, MatchStatus, Season, SeasonBracket, BracketTemplate } from './types'
import { formatScore } from '@/services/matchService'
import { getInitials } from './utils'

interface MatchesSectionProps {
  matches: AdminMatchSummary[]
  matchesLoading: boolean
  matchStatusFilter: MatchStatus | 'all'
  onMatchStatusFilterChange: (status: MatchStatus | 'all') => void
  // Match context lookup
  seasons: Season[]
  seasonBrackets: Record<string, SeasonBracket[]>
  templates: BracketTemplate[]
  // Actions
  approvingMatchId: string | null
  cancellingMatchId: string | null
  onApproveMatch: (match: AdminMatchSummary) => void
  onCancelMatch: (match: AdminMatchSummary) => void
}

export function MatchesSection({
  matches,
  matchesLoading,
  matchStatusFilter,
  onMatchStatusFilterChange,
  seasons,
  seasonBrackets,
  templates,
  approvingMatchId,
  cancellingMatchId,
  onApproveMatch,
  onCancelMatch,
}: MatchesSectionProps) {
  // Get season and bracket names for a match
  const getMatchContext = (match: AdminMatchSummary) => {
    const season = seasons.find(s => s.seasonId === match.seasonId)
    const bracket = seasonBrackets[match.seasonId]?.find(b => b.bracketId === match.bracketId)
    return {
      seasonName: season?.name || 'Unknown Season',
      bracketName: bracket?.name || templates.find(t => t.templateId === bracket?.templateId)?.name || 'Unknown Bracket',
    }
  }

  return (
    <Card>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Swords className="h-5 w-5" />
              Matches
            </CardTitle>
            <CardDescription>View and approve match results</CardDescription>
          </div>
          {/* Status Filter */}
          <div className="flex items-center gap-1 p-1 bg-muted rounded-lg overflow-x-auto">
            {(['all', 'pending', 'approved', 'disputed'] as const).map((status) => (
              <button
                key={status}
                onClick={() => onMatchStatusFilterChange(status)}
                className={`px-2.5 sm:px-3 py-1 text-xs font-medium rounded-md transition-colors whitespace-nowrap ${
                  matchStatusFilter === status
                    ? 'bg-background text-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {status === 'all' ? 'All' : status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {matchesLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : matches.length === 0 ? (
          <div className="text-center py-12 border-2 border-dashed rounded-lg">
            <Swords className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-30" />
            <p className="text-muted-foreground">
              {matchStatusFilter === 'all' 
                ? 'No matches recorded yet' 
                : `No ${matchStatusFilter} matches`}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {matches.map((match) => {
              const { seasonName, bracketName } = getMatchContext(match)
              return (
                <div
                  key={match.matchId}
                  className={`p-3 sm:p-4 rounded-lg border transition-colors ${
                    match.status === 'pending' 
                      ? 'border-amber-500/30 bg-amber-500/5' 
                      : match.status === 'disputed'
                      ? 'border-red-500/30 bg-red-500/5'
                      : ''
                  }`}
                >
                  {/* Mobile Layout - Stacked */}
                  <div className="sm:hidden space-y-3">
                    {/* Season/Bracket context */}
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <span className="truncate">{seasonName}</span>
                      <ChevronRight className="h-3 w-3 shrink-0" />
                      <span className="truncate font-medium text-foreground">{bracketName}</span>
                    </div>
                    
                    {/* Status and Date */}
                    <div className="flex items-center justify-between">
                      <div className={`flex items-center gap-1.5 ${
                        match.status === 'pending' ? 'text-amber-500' :
                        match.status === 'approved' ? 'text-green-500' :
                        match.status === 'disputed' ? 'text-red-500' :
                        'text-muted-foreground'
                      }`}>
                        {match.status === 'pending' && <Clock className="h-4 w-4" />}
                        {match.status === 'approved' && <CheckCircle className="h-4 w-4" />}
                        {match.status === 'disputed' && <XCircle className="h-4 w-4" />}
                        <span className="text-xs font-medium capitalize">{match.status}</span>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {new Date(match.submittedAt).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {/* Players with score */}
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <Avatar className={`h-8 w-8 shrink-0 ${match.winnerId === match.player1.userId ? 'ring-2 ring-green-500' : ''}`}>
                          <AvatarImage src={match.player1.avatar} alt={match.player1.displayName} />
                          <AvatarFallback className="text-xs">{getInitials(match.player1.displayName)}</AvatarFallback>
                        </Avatar>
                        <span className={`text-sm truncate ${match.winnerId === match.player1.userId ? 'font-semibold' : ''}`}>
                          {match.player1.displayName}
                        </span>
                      </div>
                      <span className="text-sm font-bold text-muted-foreground shrink-0 px-2">
                        {formatScore(match.sets)}
                      </span>
                      <div className="flex items-center gap-2 flex-1 min-w-0 justify-end">
                        <span className={`text-sm truncate ${match.winnerId === match.player2.userId ? 'font-semibold' : ''}`}>
                          {match.player2.displayName}
                        </span>
                        <Avatar className={`h-8 w-8 shrink-0 ${match.winnerId === match.player2.userId ? 'ring-2 ring-green-500' : ''}`}>
                          <AvatarImage src={match.player2.avatar} alt={match.player2.displayName} />
                          <AvatarFallback className="text-xs">{getInitials(match.player2.displayName)}</AvatarFallback>
                        </Avatar>
                      </div>
                    </div>
                    
                    {/* Meta and Actions */}
                    {(match.submittedBy || (match.status === 'disputed' && match.disputeReason) || match.status !== 'approved') && (
                      <div className="flex items-center justify-between pt-2 border-t gap-2">
                        <p className="text-xs text-muted-foreground truncate">
                          {match.submittedBy && <span>By {match.submittedBy}</span>}
                          {match.status === 'disputed' && match.disputeReason && (
                            <span className="text-red-500"> • {match.disputeReason}</span>
                          )}
                        </p>
                        <div className="flex gap-1.5 shrink-0">
                          {match.status === 'pending' && (
                            <Button
                              size="sm"
                              className="h-8"
                              onClick={() => onApproveMatch(match)}
                              disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                            >
                              {approvingMatchId === match.matchId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Check className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {match.status === 'disputed' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8"
                              onClick={() => onApproveMatch(match)}
                              disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                            >
                              {approvingMatchId === match.matchId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                'Resolve'
                              )}
                            </Button>
                          )}
                          {match.status !== 'approved' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 text-destructive hover:text-destructive hover:bg-destructive/10"
                              onClick={() => onCancelMatch(match)}
                              disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                            >
                              {cancellingMatchId === match.matchId ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <X className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Desktop Layout - Horizontal */}
                  <div className="hidden sm:flex sm:items-center sm:gap-3">
                    {/* Status indicator */}
                    <div className={`shrink-0 ${
                      match.status === 'pending' ? 'text-amber-500' :
                      match.status === 'approved' ? 'text-green-500' :
                      match.status === 'disputed' ? 'text-red-500' :
                      'text-muted-foreground'
                    }`}>
                      {match.status === 'pending' && <Clock className="h-5 w-5" />}
                      {match.status === 'approved' && <CheckCircle className="h-5 w-5" />}
                      {match.status === 'disputed' && <XCircle className="h-5 w-5" />}
                    </div>

                    {/* Season/Bracket context */}
                    <div className="shrink-0 flex items-center gap-1 text-xs text-muted-foreground min-w-[140px] max-w-[180px]">
                      <span className="truncate">{seasonName}</span>
                      <ChevronRight className="h-3 w-3 shrink-0" />
                      <span className="truncate font-medium text-foreground">{bracketName}</span>
                    </div>

                    {/* Players & Score */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <div className="flex items-center gap-1.5">
                          <Avatar className={`h-6 w-6 ${match.winnerId === match.player1.userId ? 'ring-2 ring-green-500' : ''}`}>
                            <AvatarImage src={match.player1.avatar} alt={match.player1.displayName} />
                            <AvatarFallback className="text-[10px]">{getInitials(match.player1.displayName)}</AvatarFallback>
                          </Avatar>
                          <span className={`text-sm ${match.winnerId === match.player1.userId ? 'font-semibold' : ''}`}>
                            {match.player1.displayName}
                          </span>
                        </div>
                        <span className="text-sm font-bold text-muted-foreground">
                          {formatScore(match.sets)}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <Avatar className={`h-6 w-6 ${match.winnerId === match.player2.userId ? 'ring-2 ring-green-500' : ''}`}>
                            <AvatarImage src={match.player2.avatar} alt={match.player2.displayName} />
                            <AvatarFallback className="text-[10px]">{getInitials(match.player2.displayName)}</AvatarFallback>
                          </Avatar>
                          <span className={`text-sm ${match.winnerId === match.player2.userId ? 'font-semibold' : ''}`}>
                            {match.player2.displayName}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {new Date(match.submittedAt).toLocaleDateString()}
                        {match.submittedBy && <span> • Submitted by {match.submittedBy}</span>}
                        {match.status === 'disputed' && match.disputeReason && (
                          <span className="text-red-500"> • {match.disputeReason}</span>
                        )}
                      </p>
                    </div>
                    
                    {/* Actions */}
                    <div className="flex gap-2">
                      {match.status === 'pending' && (
                        <Button
                          size="sm"
                          onClick={() => onApproveMatch(match)}
                          disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                        >
                          {approvingMatchId === match.matchId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <Check className="h-4 w-4 mr-1" />
                              Approve
                            </>
                          )}
                        </Button>
                      )}
                      {match.status === 'disputed' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => onApproveMatch(match)}
                          disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                        >
                          {approvingMatchId === match.matchId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            'Resolve'
                          )}
                        </Button>
                      )}
                      {/* Delete button - always available for non-approved matches */}
                      {match.status !== 'approved' && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          onClick={() => onCancelMatch(match)}
                          disabled={approvingMatchId === match.matchId || cancellingMatchId === match.matchId}
                        >
                          {cancellingMatchId === match.matchId ? (
                            <Loader2 className="h-4 w-4 animate-spin" />
                          ) : (
                            <>
                              <X className="h-4 w-4 mr-1" />
                              Delete
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
